import { isNull, isObject, isString } from "@/shared/libs/type-utils";
import type { ReactNode } from "react";

// Define the Responsive type for breakpoint-specific values
type Responsive<T> = T | { default?: T; sm?: T; md?: T; lg?: T; xl?: T };

interface TypographyProps {
  variant?: Responsive<
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body1"
    | "body2"
    | "body3"
    | "caption"
  >;
  fontWeight?: Responsive<FontWeight>;
  lineHeight?: Responsive<LineHeight>;
  children: ReactNode;
  className?: string;
}

type FontWeight = "normal" | "medium" | "semibold";
type LineHeight = "normal" | "tight" | "relaxed" | "loose";

const getResponsiveClass = <T extends string>(
  prop: Responsive<T> | undefined,
  mapper: (value: T) => string,
  defaultValue: T,
): string => {
  if (!prop) return "";
  if (isString(prop)) return mapper(prop as T);

  return Object.entries(prop)
    .map(([breakpoint, value]) =>
      breakpoint === "default"
        ? mapper(value as T)
        : `${breakpoint}:${mapper(value as T)}`,
    )
    .join(" ");
};

const Typography: React.FC<TypographyProps> = ({
  variant = "body2",
  children,
  fontWeight = "normal",
  lineHeight = "normal",
  className = "",
}) => {
  const Tag = getVariantTag(
    typeof variant === "string" ? variant : variant.sm || "body2", // Use first defined breakpoint or fallback
  );

  return (
    <Tag
      className={getVariantClasses(variant, fontWeight, lineHeight, className)}
    >
      {children}
    </Tag>
  );
};

const getVariantTag = (variant: TypographyProps["variant"]) => {
  const resolvedVariant =
    typeof variant === "string" ? variant : variant?.sm || "body2";
  switch (resolvedVariant) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return resolvedVariant;
    case "body1":
    case "body2":
    case "body3":
      return "p";
    case "caption":
      return "span";
    default:
      return "p";
  }
};

const getFontWeight = (variant: FontWeight) => {
  switch (variant) {
    case "normal":
      return "font-normal";
    case "medium":
      return "font-medium";
    case "semibold":
      return "font-semibold";
    default:
      return "font-normal";
  }
};

const getLineHeight = (lineHeight: LineHeight) => {
  switch (lineHeight) {
    case "normal":
      return "leading-normal";
    case "tight":
      return "leading-tight";
    case "relaxed":
      return "leading-relaxed";
    case "loose":
      return "leading-loose";
    default:
      return "leading-normal";
  }
};

const getVariantClasses = (
  variant: TypographyProps["variant"],
  fontWeight: TypographyProps["fontWeight"],
  lineHeight: TypographyProps["lineHeight"],
  className: string,
) => {
  const baseClasses = {
    h1: "text-2xl font-semibold",
    h2: "text-xl font-semibold",
    h3: "text-lg font-semibold",
    h4: "text-base font-semibold",
    h5: "text-base font-medium",
    h6: "text-base font-medium",
    body1: "text-base font-normal",
    body2: "text-sm font-medium",
    body3: "text-sm font-medium",
    caption: "text-xs",
  };

  // Generate responsive classes for each prop
  const variantClasses = getResponsiveClass(
    variant,
    (v) => baseClasses[v || "body2"],
    "body2",
  );
  const fontWeightClasses = getResponsiveClass(
    fontWeight,
    getFontWeight,
    "normal",
  );
  const lineHeightClasses = getResponsiveClass(
    lineHeight,
    getLineHeight,
    "normal",
  );

  return `${variantClasses} ${fontWeightClasses} ${lineHeightClasses} ${className}`.trim();
};

export default Typography;
